import React from 'react';
import {
  View,
  Text,
  StyleSheet,
  Linking,
} from 'react-native';
import { createThemedStyles } from '../theme/useThemedStyles';
import AdBanner from '../components/AdBanner';
import WebPage from '../components/web/WebPage';
import PageScroll from '../components/web/PageScroll';

const TermsPrivacyScreen = () => {
  const styles = useTermsPrivacyStyles();

  const handleEmailPress = () => {
    Linking.openURL('mailto:sales@expensewatcher.com');
  };

  return (
    <WebPage title="Terms & Privacy">
    <PageScroll style={styles.container}>
      {/* Terms of Use Section */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>📋 Terms of Use</Text>
        <Text style={styles.date}>Effective Date: May 31, 2025</Text>

        <View style={styles.contentItem}>
          <Text style={styles.contentTitle}>1. Acceptance of Terms</Text>
          <Text style={styles.contentText}>
            By using the GoalMint mobile or Web application ("App"), you agree to these Terms of Use and our Privacy Policy. If you do not agree, please do not use the App.
          </Text>
        </View>

        <View style={styles.contentItem}>
          <Text style={styles.contentTitle}>2. App Purpose</Text>
          <Text style={styles.contentText}>
            GoalMint is a personal finance tool designed to help users plan, track, and allocate income toward financial goals. The App provides projections and suggestions based on user inputs, but it does not offer financial, investment, or tax advice.
          </Text>
        </View>

        <View style={styles.contentItem}>
          <Text style={styles.contentTitle}>3. User Accounts</Text>
          <Text style={styles.contentText}>
            To use some features of the App, you may need to create an account. You agree to provide accurate information and keep your login credentials secure.
          </Text>
        </View>

        <View style={styles.contentItem}>
          <Text style={styles.contentTitle}>4. User Responsibilities</Text>
          <Text style={styles.contentText}>
            You are responsible for the accuracy of any financial data you input. GoalMint is not liable for decisions you make based on app suggestions.
          </Text>
        </View>

        <View style={styles.contentItem}>
          <Text style={styles.contentTitle}>5. Intellectual Property</Text>
          <Text style={styles.contentText}>
            All content, design, logos, and code are the property of GoalMint and its creators. You may not reproduce or distribute any part of the App without permission.
          </Text>
        </View>

        <View style={styles.contentItem}>
          <Text style={styles.contentTitle}>6. Termination</Text>
          <Text style={styles.contentText}>
            We may suspend or terminate your access if you violate these Terms. You can delete your account at any time via the Settings menu.
          </Text>
        </View>

        <View style={styles.contentItem}>
          <Text style={styles.contentTitle}>7. Limitation of Liability</Text>
          <Text style={styles.contentText}>
            GoalMint is provided "as is" without warranties. We are not liable for any loss or damages arising from use of the App.
          </Text>
        </View>

        <View style={styles.contentItem}>
          <Text style={styles.contentTitle}>8. Changes to Terms</Text>
          <Text style={styles.contentText}>
            We may update these Terms from time to time. Continued use of the App means you accept the revised terms.
          </Text>
        </View>

        <View style={styles.contentItem}>
          <Text style={styles.contentTitle}>9. Contact</Text>
          <Text style={styles.contentText}>
            For questions or concerns, email us at{' '}
            <Text style={styles.link} onPress={handleEmailPress}>
              sales@expensewatcher.com
            </Text>
          </Text>
        </View>
      </View>

      {/* Privacy Policy Section */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>🔐 Privacy Policy</Text>
        <Text style={styles.date}>Effective Date: May 31, 2025</Text>

        <View style={styles.contentItem}>
          <Text style={styles.contentTitle}>1. Information We Collect</Text>
          <Text style={styles.contentText}>
            • User-Provided Data: When you use GoalMint, you may provide financial goals, income data, and other personal financial preferences.{'\n'}
            • Account Info: Name, email, password.{'\n'}
            • Device Data: We may collect anonymized data such as device type and usage analytics to improve performance.
          </Text>
        </View>

        <View style={styles.contentItem}>
          <Text style={styles.contentTitle}>2. How We Use Your Information</Text>
          <Text style={styles.contentText}>
            • To provide goal tracking and income allocation features.{'\n'}
            • To personalize user experience.{'\n'}
            • To improve our App's functionality and user support.
          </Text>
        </View>

        <View style={styles.contentItem}>
          <Text style={styles.contentTitle}>3. Data Sharing</Text>
          <Text style={styles.contentText}>
            • We do NOT sell your data.{'\n'}
            • We may share anonymous usage data with analytics providers to help improve the app.{'\n'}
            • We comply with law enforcement requests where legally required.
          </Text>
        </View>

        <View style={styles.contentItem}>
          <Text style={styles.contentTitle}>4. Data Security</Text>
          <Text style={styles.contentText}>
            We use encryption and secure storage practices to protect your data. You can enable biometric login for extra security.
          </Text>
        </View>

        <View style={styles.contentItem}>
          <Text style={styles.contentTitle}>5. Your Rights</Text>
          <Text style={styles.contentText}>
            • You can view, edit, or delete your data at any time.{'\n'}
            • You can export your data from the Settings {'>'} Export Data section.{'\n'}
            • You can delete your account and all associated data from within the app.
          </Text>
        </View>

        <View style={styles.contentItem}>
          <Text style={styles.contentTitle}>6. Children's Privacy</Text>
          <Text style={styles.contentText}>
            GoalMint is not intended for use by children under 13. We do not knowingly collect data from children.
          </Text>
        </View>

        <View style={styles.contentItem}>
          <Text style={styles.contentTitle}>7. Changes to This Policy</Text>
          <Text style={styles.contentText}>
            We may update this Privacy Policy. We'll notify you of significant changes through the app or email (if applicable).
          </Text>
        </View>

        <View style={styles.contentItem}>
          <Text style={styles.contentTitle}>8. Contact</Text>
          <Text style={styles.contentText}>
            Questions? Contact us at{' '}
            <Text style={styles.link} onPress={handleEmailPress}>
              sales@expensewatcher.com
            </Text>
          </Text>
        </View>
      </View>
      <AdBanner />
    </PageScroll>
    </WebPage>
  );
};

const useTermsPrivacyStyles = createThemedStyles((colors) => ({
  container: {
    flex: 1,
    backgroundColor: colors.grey[100],
    padding: 16,
  },
  title: {
    fontSize: 24,
    fontWeight: 'bold',
    color: colors.primary.main,
    marginBottom: 24,
  },
  section: {
    marginBottom: 24,
    backgroundColor: colors.surface,
    borderRadius: 12,
    padding: 16,
  },
  sectionTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: colors.primary.main,
    marginBottom: 16,
  },
  date: {
    fontSize: 14,
    color: colors.text.secondary,
    marginBottom: 24,
  },
  contentItem: {
    marginBottom: 16,
  },
  contentTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: colors.text.primary,
    marginBottom: 8,
  },
  contentText: {
    fontSize: 14,
    color: colors.text.secondary,
    lineHeight: 20,
  },
  link: {
    color: colors.primary.main,
    textDecorationLine: 'underline',
  },
}));

export default TermsPrivacyScreen; 